import type {
    AlertaCritica,
    CertificadosPeriodoResumen,
    DashboardKpis,
    DashboardResumen,
    DictamenDistribucion,
    ExamenesMedicosResumen
} from '../types';

export const EMPTY_DASHBOARD_KPIS: DashboardKpis = {
    empleados_activos: 0,
    tasa_ausentismo: 0,
    dias_incapacidad: 0,
    cumplimiento_porcentaje: 0,
    restricciones_activas: 0,
    casos_incapacidad: 0
};

export const EMPTY_EXAMENES_MEDICOS: ExamenesMedicosResumen = {
    al_dia: 0,
    proximos_30_dias: 0,
    vencidos: 0,
    sin_examen: 0,
    vencidos_o_sin_registro: 0,
    total: 0
};

export const EMPTY_CERTIFICADOS_PERIODO: CertificadosPeriodoResumen = {
    pre_ingreso_emitidos: 0,
    periodicos_emitidos: 0,
    no_apto: 0
};

export const EMPTY_DICTAMEN_DISTRIBUCION: DictamenDistribucion = {
    apto: 0,
    apto_restricciones: 0,
    no_apto: 0,
    pendiente: 0,
    sin_certificado: 0
};

const toNumber = (value: unknown, fallback = 0): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const unwrapPayload = (raw: unknown): Record<string, unknown> => {
    if (!raw || typeof raw !== 'object') {
        return {};
    }

    const record = raw as Record<string, unknown>;

    if (record.data && typeof record.data === 'object') {
        return record.data as Record<string, unknown>;
    }

    return record;
};

const normalizeKpis = (raw: unknown, payload: Record<string, unknown> = {}): DashboardKpis => {
    const ausentismo =
        payload.ausentismo && typeof payload.ausentismo === 'object'
            ? (payload.ausentismo as Record<string, unknown>)
            : {};
    const incapacidad =
        payload.incapacidad && typeof payload.incapacidad === 'object'
            ? (payload.incapacidad as Record<string, unknown>)
            : {};
    const kpis =
        raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : ({} as Record<string, unknown>);

    return {
        empleados_activos: toNumber(kpis.empleados_activos ?? payload.empleados_activos),
        tasa_ausentismo: toNumber(
            kpis.tasa_ausentismo ?? ausentismo.tasa_porcentaje ?? payload.tasa_ausentismo
        ),
        dias_incapacidad: toNumber(
            kpis.dias_incapacidad ??
                incapacidad.dias_perdidos_totales ??
                ausentismo.dias_totales ??
                payload.dias_incapacidad
        ),
        cumplimiento_porcentaje: toNumber(kpis.cumplimiento_porcentaje),
        restricciones_activas: toNumber(kpis.restricciones_activas),
        casos_incapacidad: toNumber(
            kpis.casos_incapacidad ??
                incapacidad.casos_activos ??
                ausentismo.empleados_afectados
        )
    };
};

const normalizeExamenes = (raw: unknown): ExamenesMedicosResumen => {
    if (!raw || typeof raw !== 'object') {
        return {...EMPTY_EXAMENES_MEDICOS};
    }

    const examenes = raw as Record<string, unknown>;
    const alDia = toNumber(examenes.al_dia);
    const proximos = toNumber(examenes.proximos_30_dias);
    const vencidos = toNumber(examenes.vencidos);
    const sinExamen = toNumber(examenes.sin_examen);
    const vencidosOSin = toNumber(examenes.vencidos_o_sin_registro, vencidos + sinExamen);
    const total = toNumber(examenes.total, alDia + proximos + vencidos + sinExamen);

    return {
        al_dia: alDia,
        proximos_30_dias: proximos,
        vencidos,
        sin_examen: sinExamen,
        vencidos_o_sin_registro: vencidosOSin,
        total: total > 0 ? total : alDia + proximos + vencidos + sinExamen
    };
};

const normalizeCertificados = (raw: unknown): CertificadosPeriodoResumen => {
    if (!raw || typeof raw !== 'object') {
        return {...EMPTY_CERTIFICADOS_PERIODO};
    }

    const certificados = raw as Record<string, unknown>;

    return {
        pre_ingreso_emitidos: toNumber(certificados.pre_ingreso_emitidos),
        periodicos_emitidos: toNumber(certificados.periodicos_emitidos),
        no_apto: toNumber(certificados.no_apto)
    };
};

const normalizeDictamenDistribucion = (raw: unknown): DictamenDistribucion => {
    if (!raw || typeof raw !== 'object') {
        return {...EMPTY_DICTAMEN_DISTRIBUCION};
    }

    const dist = raw as Record<string, unknown>;

    return {
        apto: toNumber(dist.apto),
        apto_restricciones: toNumber(
            dist.apto_restricciones ?? dist.apto_con_restricciones
        ),
        no_apto: toNumber(dist.no_apto),
        pendiente: toNumber(dist.pendiente),
        sin_certificado: toNumber(dist.sin_certificado)
    };
};

const normalizeAlertas = (raw: unknown): AlertaCritica[] => {
    if (!Array.isArray(raw)) {
        return [];
    }

    return raw
        .filter(item => item && typeof item === 'object')
        .map((item, index) => {
            const alerta = item as Record<string, unknown>;
            const severidadRaw = String(alerta.severidad ?? '').toLowerCase();
            const severidad: AlertaCritica['severidad'] =
                severidadRaw === 'alta' || severidadRaw === 'critical' || severidadRaw === 'urgent'
                    ? 'alta'
                    : severidadRaw === 'media' || severidadRaw === 'warning'
                      ? 'media'
                      : 'baja';

            return {
                id: String(alerta.id ?? `alerta-${index}`),
                tipo: String(alerta.tipo ?? 'general'),
                severidad,
                mensaje: String(alerta.mensaje ?? 'Alerta'),
                fecha: String(alerta.fecha ?? ''),
                empleado_id: alerta.empleado_id != null ? String(alerta.empleado_id) : null,
                empleado_nombre:
                    alerta.empleado_nombre != null ? String(alerta.empleado_nombre) : null,
                ruta: alerta.ruta != null ? String(alerta.ruta) : null
            };
        });
};

/** Normaliza respuestas parciales o anidadas de `/portal-cliente/dashboard/resumen`. */
export const normalizeDashboardResumen = (raw: unknown): DashboardResumen => {
    const payload = unwrapPayload(raw);
    const periodo =
        payload.periodo && typeof payload.periodo === 'object'
            ? (payload.periodo as Record<string, unknown>)
            : null;

    return {
        empresa_id: String(payload.empresa_id ?? ''),
        empresa_nombre: String(payload.empresa_nombre ?? 'Portal Cliente'),
        periodo_referencia: payload.periodo_referencia
            ? String(payload.periodo_referencia)
            : periodo?.desde && periodo?.hasta
              ? `${periodo.desde} — ${periodo.hasta}`
              : undefined,
        kpis: normalizeKpis(payload.kpis, payload),
        examenes_medicos: normalizeExamenes(payload.examenes_medicos),
        certificados_periodo: normalizeCertificados(
            payload.certificados_periodo ?? payload.certificados
        ),
        dictamen_distribucion: normalizeDictamenDistribucion(payload.dictamen_distribucion),
        alertas: normalizeAlertas(payload.alertas)
    };
};
